import { FilteredListViewModel as BaseListViewModel } from "@frui.ts/datascreens";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import type { ScreenBase } from "@frui.ts/screens";
import { BusyWatcher } from "@frui.ts/screens";
import { computed, toJS } from "mobx";
import type { EntityNames } from "config";
import { newNavigationName } from "config";
import type { IPagedResults, IResults } from "data/types";
import type { IDeleteRepository, IItemsRepository, IListRepository } from "repositories/types";
import type ConfirmationService from "services/confirmationService";
import type { DeletedEvent } from "services/eventBus";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type NotificationService from "services/notificationService";
import { NotificationType } from "services/notificationService";
import type SecurityService from "services/securityService";
import {
  formatIntervalObjectValues,
  isNestedObjectValue,
  isNumberValue,
  parseBankAccountObjectValue,
  parseDateValue,
  parseIntervalObjectValue,
  serializeFilters,
} from "utils/filters";
import { isDateProperty, isNumberProperty } from "utils/propertyUtils";
import { watchBusy } from "utils/busyWatcher";

function withDeleteRepository<TEntity>(repository: any = {}): repository is IDeleteRepository<TEntity> {
  return "delete" in repository;
}

export function isPagedResults<TEntity>(response: any = {}): response is IPagedResults<TEntity> {
  return "metadata" in response;
}

export function isListRepository<TEntity>(repository: any = {}): repository is IListRepository<TEntity> {
  return repository.getAll.length >= 1;
}

export function isItemsRepository<TEntity>(repository: any = {}): repository is IItemsRepository<TEntity> {
  return "getAll" in repository;
}

export default abstract class ListViewModel<
  TEntity,
  TDetail extends ScreenBase = ScreenBase,
  TCreate extends ScreenBase = ScreenBase,
  TFilter = any
> extends BaseListViewModel<TEntity, TFilter, TDetail | TCreate | ScreenBase> {
  idProperty: PropertyName<TEntity> = "id" as any;

  protected createViewModelFactory?: (id: any) => TCreate;
  protected detailViewModelFactory?: (id: any, ...rest: any) => TDetail;
  /* tslint:disable max-union-size */
  protected repository?:
    | IListRepository<TEntity>
    | IItemsRepository<TEntity>
    | (IListRepository<TEntity> & IDeleteRepository<TEntity>)
    | (IItemsRepository<TEntity> & IDeleteRepository<TEntity>)
    | any;
  /* tslint:enable max-union-size */
  protected notificationService?: NotificationService;
  protected confirmationService?: ConfirmationService;
  protected abstract readonly modelName: EntityNames;
  protected abstract localizationService: LocalizationService;
  protected abstract eventBus: EventBus;
  protected abstract securityService: SecurityService;
  protected unsubscriptions: Array<() => void> = [];
  loadTimeout: NodeJS.Timeout;
  busyWatcher = new BusyWatcher();

  abstract translate(code: string, size?: number): string;

  constructor() {
    super();
    this.items = [];
  }

  onInitialize() {
    if (this.allowDelete && this.repository && withDeleteRepository(this.repository) && !this.confirmationService) {
      console.warn("Provide ConfirmationService to delete confirmation");
    }

    if (this.allowDelete && this.repository && withDeleteRepository(this.repository) && !this.notificationService) {
      console.warn("Provide NotificationService to notify about delete success");
    }

    this.unsubscriptions.push(this.eventBus.subscribe(`${this.modelName}.*`, this.loadData));
    this.loadTimeout = setTimeout(this.resetFilterAndLoad, 50);

    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected fetchData(): Promise<IPagedResults<TEntity>> | Promise<IResults<TEntity>> {
    if (this.repository && isListRepository<TEntity>(this.repository)) {
      const { limit, offset } = this.pagingFilter;
      return this.repository.getAll({ offset, limit }, this.appliedFilter);
    } else if (this.repository && isItemsRepository<TEntity>(this.repository)) {
      return this.repository.getAll();
    } else {
      throw new Error(
        "Unknown repository type. You have to overload fetchData method to specify how to load items from yours repository"
      );
    }
  }

  // Overload when you need to do something with data before its usage
  protected processData(items: TEntity[]) {
    return items;
  }

  @bound
  @watchBusy
  async loadData() {
    this.notifyNavigationChanged(); // save filter to history
    const result = await this.fetchData();

    if (isPagedResults<TEntity>(result)) {
      const {
        metadata: { limit, offset, total: totalItems },
        items,
      } = result;
      this.setData([this.processData(items), { limit, offset, totalItems }]);
    } else {
      const { items } = result;
      this.setData([this.processData(items), { limit: items.length, offset: 0, totalItems: items.length }]);
    }
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get allowDelete() {
    return this.allowWrite;
  }

  @computed
  get allowWrite() {
    return this.securityService.allowToEntity(this.modelName, "write");
  }

  @computed
  get allowRead() {
    return this.securityService.allowToEntity(this.modelName, "read");
  }

  @bound
  allowItemDetail(item: TEntity) {
    return true;
  }

  @bound
  allowItemDelete(item: TEntity) {
    return true;
  }

  // In Frui.ts is an error  - reset clear validators too
  protected resetFilterValues(filter: TFilter) {
    for (const property in filter) {
      if (property !== "__validation" && property !== "__dirtycheck") {
        filter[property] = undefined as any;
      }
    }
  }

  @bound
  onAddClick() {
    const createViewModelFactory = this.createViewModelFactory;
    if (createViewModelFactory && this.allowWrite) {
      return () => this.tryActivateChild(createViewModelFactory(undefined));
    } else {
      return undefined;
    }
  }

  @bound
  onItemClick() {
    const detailFactory = this.detailViewModelFactory;
    if (detailFactory) {
      return (item: TEntity) =>
        this.tryActivateChild(detailFactory(item[this.idProperty], ...this.detailParams(item[this.idProperty])));
    } else {
      return undefined;
    }
  }

  delete(item: TEntity) {
    const repository = this.repository;

    if (withDeleteRepository<TEntity>(repository) && this.allowDelete) {
      return repository.delete(item);
    } else if (this.allowDelete) {
      throw new Error("You have to overload delete method to specify how to delete your items");
    }
  }

  @bound
  onDeleteClick() {
    const repository = this.repository;
    if ((withDeleteRepository<TEntity>(repository) && this.allowDelete) || this.allowDelete) {
      return async (item: TEntity) => {
        const confirmed = this.confirmationService ? await this.confirmationService.deleteConfirmation() : true;

        if (confirmed) {
          try {
            await this.delete(item);
            this.onDeleted(item);
          } catch (e) {}
        }
      };
    } else {
      return undefined;
    }
  }

  protected onDeleted(item: TEntity) {
    if (this.notificationService) {
      this.notificationService.add({ title: this.translate("deleted"), type: NotificationType.SUCCESS });
    }
    this.eventBus.publish<DeletedEvent<TEntity>>({ type: `${this.modelName}.deleted`, payload: { [this.modelName]: item } });
  }

  async findNavigationChild(navigationName?: string) {
    if (navigationName === newNavigationName) {
      return this.createViewModelFactory ? this.createViewModelFactory(undefined) : undefined;
    } else if (navigationName) {
      return this.detailViewModelFactory
        ? this.detailViewModelFactory(navigationName, ...this.detailParams(navigationName))
        : undefined;
    }

    return Promise.resolve(undefined);
  }

  // Overload to pass params to detailViewModelFactory
  protected detailParams(itemId: any): any[] {
    return [];
  }

  get navigationParams() {
    return {
      ...this.navigationParamsValue,
      ...formatIntervalObjectValues(serializeFilters(toJS(this.appliedFilter), false)),
      offset: this.pagingFilter.offset,
    };
  }

  async navigate(subPath: string | undefined, params: any) {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }

    if (params) {
      Object.keys(this.filter).forEach(key => {
        const filterParam: keyof TFilter = key as any;
        if (params[filterParam]) {
          this.filter[filterParam] = this.parseFilterParam(filterParam, params[filterParam]);
        }
      });

      this.applyFilter();
    }

    if (params?.offset) {
      this.pagingFilter.offset = +parseInt(params.offset);
    }

    await this.loadData();
    return super.navigate(subPath, params);
  }

  // Overload this if need custom logic of parsing filter form URL params
  protected parseFilterParam(paramName: keyof TFilter | string, value: any) {
    const propertyName = paramName as string;

    if (!isNestedObjectValue(value) && isDateProperty(propertyName)) {
      return parseDateValue(value);
    }

    if (!isNestedObjectValue(value) && isNumberValue(value) && isNumberProperty(propertyName)) {
      return parseFloat(value);
    }

    // Parse nested objects
    if (isNestedObjectValue(value) && propertyName !== "bankAccount") {
      return parseIntervalObjectValue(value);
    } else if (isNestedObjectValue(value) && propertyName === "bankAccount") {
      return parseBankAccountObjectValue(value);
    }

    // Parse boolean
    if (value === "false") {
      return false;
    }

    // Parse boolean
    if (value === "true") {
      return true;
    }

    return value;
  }

  translateAttribute(attribute: PropertyName<TEntity>, size?: number) {
    return this.localizationService.translateAttribute(this.modelName, attribute, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }
}
